import React from 'react';

const AIDisclaimers: React.FC = () => {
  return (
    <div className="ai-disclaimers-page" style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* Header Section */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '4rem 0 2rem',
        marginBottom: '3rem'
      }}>
        <div className="container">
          <div className="text-center text-white">
            <h1 className="display-4 fw-bold mb-3">
              <i className="fas fa-robot me-3"></i>
              AI Disclaimers & Responsible AI Notices
            </h1>
            <p className="lead mb-2">
              Important information about our AI-powered features and their limitations.
            </p>
            <p className="mb-0 opacity-75">
              <small>Status: Draft | BRAINTONICA EDTECH LLP — WiZiAi</small>
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mb-5">
        <div className="card shadow-lg border-0 rounded-4">
          <div className="card-body p-5">
            
            {/* Introduction Alert */}
            <div className="alert alert-warning border-0 shadow-sm mb-5">
              <h4 className="alert-heading mb-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Understanding AI-Powered Features
              </h4>
              <p className="mb-0">
                WiZiAi uses artificial intelligence to enhance your learning experience. Please read these disclaimers carefully to understand how AI features work, their capabilities, and their limitations.
              </p>
            </div>

            {/* Nature of AI Outputs */}
            <section className="mb-5">
              <h2 className="h3 fw-bold mb-3" style={{ color: '#667eea' }}>
                <i className="fas fa-brain me-2"></i>
                3.1 Nature of AI-generated Outputs
              </h2>
              <p className="text-muted mb-0">
                Many Platform features rely on machine learning and AI models to analyze performance and recommend study plans and predicted scores. AI outputs are generated from patterns in available data and statistical models; they are <strong>probabilistic and not infallible</strong>.
              </p>
            </section>

            {/* Accuracy & Predictions */}
            <section className="mb-5">
              <h2 className="h3 fw-bold mb-3" style={{ color: '#667eea' }}>
                <i className="fas fa-chart-line me-2"></i>
                3.2 Accuracy, Predictions & No Guarantees
              </h2>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="card border-warning h-100">
                    <div className="card-body">
                      <h5 className="h6 fw-bold mb-2">
                        <i className="fas fa-exclamation-circle text-warning me-2"></i>
                        Estimates Only
                      </h5>
                      <p className="mb-0 small text-muted">
                        Predictions, score estimates and admission likelihoods provided by WiZiAi are estimates only and should not be treated as guarantees or assurances of future performance.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card border-danger h-100">
                    <div className="card-body">
                      <h5 className="h6 fw-bold mb-2">
                        <i className="fas fa-user-check text-danger me-2"></i>
                        Human Verification Required
                      </h5>
                      <p className="mb-0 small text-muted">
                        Users must not rely solely on AI outputs for high-stakes decisions (e.g., admissions, career changes). Always seek human verification for such decisions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Training Data & Biases */}
            <section className="mb-5">
              <h2 className="h3 fw-bold mb-3" style={{ color: '#667eea' }}>
                <i className="fas fa-database me-2"></i>
                3.3 Training Data & Biases
              </h2>
              <ol className="text-muted">
                <li className="mb-3">
                  AI models are trained on historical, partner-provided, synthetic, and/or anonymized user data which may reflect <strong>biases or gaps</strong>. As a result, outputs may be skewed for underrepresented groups or uncommon learning patterns.
                </li>
                <li className="mb-0">
                  WiZiAi takes steps to identify and mitigate bias (model audits, fairness testing, dataset balancing) but <strong>cannot guarantee elimination of all bias</strong>.
                </li>
              </ol>
              <div className="alert alert-info border-0 mt-3 mb-0">
                <i className="fas fa-lightbulb me-2"></i>
                We continuously work to improve our models and reduce biases through regular audits and updates.
              </div>
            </section>

            {/* Human Oversight */}
            <section className="mb-5">
              <h2 className="h3 fw-bold mb-3" style={{ color: '#667eea' }}>
                <i className="fas fa-user-shield me-2"></i>
                3.4 Human Oversight, Appeals & Corrections
              </h2>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <div className="card bg-light border-0 h-100">
                    <div className="card-body">
                      <h5 className="h6 fw-bold mb-2">
                        <i className="fas fa-flag text-primary me-2"></i>
                        Flag AI Outputs
                      </h5>
                      <p className="mb-0 small text-muted">
                        Users may flag AI outputs (e.g., an incorrect predicted score or wrong personalisation). WiZiAi will investigate flagged items and provide human review where appropriate.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card bg-light border-0 h-100">
                    <div className="card-body">
                      <h5 className="h6 fw-bold mb-2">
                        <i className="fas fa-user-tie text-primary me-2"></i>
                        Human Review
                      </h5>
                      <p className="mb-0 small text-muted">
                        For decisions that materially affect users (e.g., counselling outcomes), WiZiAi reserves the right to require human counsellor review before finalising advice.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* User Consent */}
            <section className="mb-5">
              <h2 className="h3 fw-bold mb-3" style={{ color: '#667eea' }}>
                <i className="fas fa-check-circle me-2"></i>
                3.5 User Consent for Automated Profiling & Scoring
              </h2>
              <p className="text-muted mb-0">
                By using WiZiAi, users consent to automated analysis and profiling for personalisation, scoring, and product improvement. Where required by law for profiling or automated decision-making, WiZiAi will provide a clear explanation and allow users to request human intervention.
              </p>
            </section>

            {/* Explainability */}
            <section className="mb-5">
              <h2 className="h3 fw-bold mb-3" style={{ color: '#667eea' }}>
                <i className="fas fa-info-circle me-2"></i>
                3.6 Explainability, Logging & Model Updates
              </h2>
              <ol className="text-muted">
                <li className="mb-2">
                  WiZiAi logs model inputs, outputs, and key metadata for debugging, improvement, compliance, and auditing purposes.
                </li>
                <li className="mb-2">
                  Users may request an explanation of a specific automated decision (e.g., why a particular score prediction was made) and WiZiAi will provide a human-readable summary of the factors that contributed to the output, to the extent feasible.
                </li>
                <li className="mb-0">
                  Models are periodically retrained; retraining may change recommendations. WiZiAi will maintain versioning records for major model updates.
                </li>
              </ol>
            </section>

            {/* Safety & Misuse */}
            <section className="mb-5">
              <h2 className="h3 fw-bold mb-3" style={{ color: '#667eea' }}>
                <i className="fas fa-shield-alt me-2"></i>
                3.7 Safety, Misuse & Liability
              </h2>
              <ol className="text-muted">
                <li className="mb-2">
                  AI outputs must not be used to facilitate illegal activity, discrimination, or harm. WiZiAi disclaims liability for misuse of the AI features.
                </li>
                <li className="mb-0">
                  WiZiAi's liability arising from AI outputs is limited as per the Terms. Users are responsible for decisions made based on AI outputs.
                </li>
              </ol>
            </section>

            {/* How to Use AI Outputs */}
            <section className="mb-5">
              <h2 className="h3 fw-bold mb-3" style={{ color: '#667eea' }}>
                <i className="fas fa-lightbulb me-2"></i>
                3.8 How Users Should Use AI Outputs (Guidance)
              </h2>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="card border-success h-100">
                    <div className="card-body">
                      <div className="d-flex align-items-start">
                        <i className="fas fa-check-circle text-success me-3 mt-1"></i>
                        <div>
                          <strong>Treat as Guidance</strong>
                          <p className="mb-0 small text-muted">Treat AI outputs as guidance, not definitive authority.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card border-success h-100">
                    <div className="card-body">
                      <div className="d-flex align-items-start">
                        <i className="fas fa-check-circle text-success me-3 mt-1"></i>
                        <div>
                          <strong>Combine with Human Judgement</strong>
                          <p className="mb-0 small text-muted">Use AI recommendations along with human judgement and tutoring/counselling resources.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card border-success h-100">
                    <div className="card-body">
                      <div className="d-flex align-items-start">
                        <i className="fas fa-check-circle text-success me-3 mt-1"></i>
                        <div>
                          <strong>Flag Anomalies</strong>
                          <p className="mb-0 small text-muted">Flag anomalies or questionable outputs for review.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card border-success h-100">
                    <div className="card-body">
                      <div className="d-flex align-items-start">
                        <i className="fas fa-check-circle text-success me-3 mt-1"></i>
                        <div>
                          <strong>Human Review for High-Stakes</strong>
                          <p className="mb-0 small text-muted">Prefer human-reviewed outputs for high-stakes decisions.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Final Notes */}
            <section className="mb-0">
              <h2 className="h3 fw-bold mb-3" style={{ color: '#667eea' }}>
                <i className="fas fa-clipboard-list me-2"></i>
                Final Notes & Assumptions
              </h2>
              <div className="card bg-light border-0">
                <div className="card-body">
                  <ol className="mb-0 text-muted">
                    <li className="mb-3">
                      <strong>Jurisdiction:</strong> These drafts assume primary application under Indian law and DPDP Act 2023 compliance. If multi-jurisdiction coverage (GDPR/CCPA) is required, add explicit cross-border, data transfer, and DSR wording.
                    </li>
                    <li className="mb-3">
                      <strong>Children/Minors:</strong> The draft includes parental consent flows for minors. If you intend to restrict to users 18+, simplify as required.
                    </li>
                    <li className="mb-3">
                      <strong>Third-party Services:</strong> Replace placeholders with specific service providers (e.g., AWS, Firebase, Stripe, Razorpay, Proctoring vendor) and confirm use of analytics/SDKs.
                    </li>
                    <li className="mb-3">
                      <strong>Grievance Officer:</strong> Insert actual contact name, address and phone.
                    </li>
                    <li className="mb-0">
                      <strong>Legal Review Recommended:</strong> This draft is a starting point — have local counsel (Indian corporate/technology/privacy lawyer) review before publishing.
                    </li>
                  </ol>
                </div>
              </div>
            </section>

          </div>
          
          {/* Footer */}
          <div className="card-footer bg-light text-center py-4">
            <p className="mb-3 text-muted">
              <i className="fas fa-file-pdf text-danger me-2"></i>
              Need a PDF copy of these disclaimers?
            </p>
            <a 
              href="/ai_disclaimers.pdf" 
              download="WiziAI_AI_Disclaimers.pdf"
              className="btn btn-primary me-3"
              style={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                border: 'none'
              }}
            >
              <i className="fas fa-download me-2"></i>
              Download PDF
            </a>
            <a 
              href="/ai_disclaimers.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-outline-secondary"
            >
              <i className="fas fa-external-link-alt me-2"></i>
              Open PDF
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIDisclaimers;
